import { Component } from '@theme/component';
import { ThemeEvents, CartAddEvent } from '@theme/events';
import { invalidateCartRead } from '@theme/utilities';

/**
 * Open/closed state for the "Bundle & Save" accordion.
 *
 * Kept at module scope so the state survives a cart section re-render: when a bundle product is
 * added, the cart section is morphed and the toggle's `aria-expanded` attribute is reset to the
 * server-rendered "false". The custom element instance itself is preserved by the morph, so we
 * re-apply this saved state after every cart update.
 *
 * @type {boolean}
 */
let bundleOpen = false;

/**
 * A custom element that manages the "Bundle & Save" accordion in the cart drawer.
 *
 * - Toggles the accordion open/closed (closed by default).
 * - Adds a bundle product to the cart and re-renders the cart section, which removes the
 *   just-added product from the bundle list automatically (it is filtered out in Liquid).
 *
 * @typedef {object} CartBundleRefs
 * @property {HTMLButtonElement} [toggle] - The accordion toggle button.
 * @property {HTMLElement} [content] - The collapsible content wrapper.
 * @extends {Component<CartBundleRefs>}
 */
class CartBundleComponent extends Component {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.#applyOpenState();
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Toggles the accordion open/closed.
   */
  toggle() {
    bundleOpen = !bundleOpen;
    this.#applyOpenState();
  }

  /**
   * Reflects the current open state onto the DOM.
   */
  #applyOpenState() {
    const toggle = this.refs.toggle ?? this.querySelector('.cart-bundle__toggle');
    const content = this.refs.content ?? this.querySelector('[data-cart-bundle-content]');

    if (toggle) toggle.setAttribute('aria-expanded', bundleOpen ? 'true' : 'false');
    if (content instanceof HTMLElement) content.hidden = !bundleOpen;
  }

  /**
   * Re-applies the saved open state after the cart section is re-rendered elsewhere.
   */
  #onCartUpdate = () => {
    // The section morph happens synchronously in the same event; re-apply afterwards.
    queueMicrotask(() => this.#applyOpenState());
  };

  /**
   * Adds a bundle product to the cart.
   * @param {number} variantId - The variant id to add.
   */
  async add(variantId) {
    const id = Number(variantId);
    if (!Number.isFinite(id) || id <= 0) return;

    const button = /** @type {HTMLButtonElement | null} */ (
      this.querySelector(`.cart-bundle__add[data-variant-id="${id}"]`)
    );
    if (button) button.disabled = true;

    // If the product offers a subscription, add it on its first selling plan.
    const sellingPlanId = Number(button?.dataset.sellingPlanId);
    /** @type {{ id: number, quantity: number, selling_plan?: number }} */
    const item = { id, quantity: 1 };
    if (Number.isFinite(sellingPlanId) && sellingPlanId > 0) {
      item.selling_plan = sellingPlanId;
    }

    try {
      invalidateCartRead();
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [item],
          sections: this.#getSectionIds().join(','),
          sections_url: window.location.pathname,
        }),
      });

      const data = await response.json();
      invalidateCartRead();

      if (data.status) {
        // Adding failed (e.g. out of stock): re-enable the button and bail.
        if (button) button.disabled = false;
        return;
      }

      this.dispatchEvent(
        new CartAddEvent({}, String(id), {
          source: 'cart-bundle-component',
          variantId: id,
          sellingPlanId: item.selling_plan || 0,
          sections: data.sections,
        })
      );
    } catch (error) {
      console.error('[cart-bundle] add to cart failed', error);
      if (button) button.disabled = false;
    }
  }

  /**
   * Collects the section ids of every cart-items-component on the page so the cart add request
   * returns fresh markup to morph.
   * @returns {string[]}
   */
  #getSectionIds() {
    const ids = new Set();
    document.querySelectorAll('cart-items-component').forEach((node) => {
      if (node instanceof HTMLElement && node.dataset.sectionId) ids.add(node.dataset.sectionId);
    });
    return Array.from(ids);
  }
}

if (!customElements.get('cart-bundle-component')) {
  customElements.define('cart-bundle-component', CartBundleComponent);
}
